import React, { useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useAxios from 'axios-hooks';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Button, CircularProgress, Box, Paper } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { green } from '@material-ui/core/colors';

import { emptyDirectSaleCartAction } from '../../redux/actions/directSaleCart.actions';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(6),
    textAlign: 'center',
  },
  icon: {
    fontSize: 80,
    color: green[500],
  },
  title: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  orderNumber: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  message: {
    marginBottom: theme.spacing(2),
  },
  details: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    textAlign: 'left',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  buttonBox: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
}));

const DirectSaleConfirmation: React.FC = () => {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const sessionId = searchParams.get('session_id');

  // Svuota carrello al mount
  useEffect(() => {
    dispatch(emptyDirectSaleCartAction());
  }, [dispatch]);

  const [{ data: order, loading, error }, refetch] = useAxios(
    sessionId ? `/stripe-orders/session/${sessionId}` : { url: '' },
    { useCache: false }
  );

  // Polling: riprova ogni 2 secondi se l'ordine è pending
  useEffect(() => {
    if (order && (order as any).pending) {
      const timer = setTimeout(() => {
        refetch();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [order, refetch]);

  if (!sessionId) {
    return (
      <Container maxWidth="sm" className={classes.container}>
        <Paper className={classes.paper}>
          <Typography variant="h5" color="error">
            Sessione non valida
          </Typography>
          <Typography variant="body1" className={classes.message}>
            Non è stato possibile recuperare i dettagli dell'ordine.
          </Typography>
          <Button variant="contained" color="primary" component={RouterLink} to="/ordina">
            Torna ai prodotti
          </Button>
        </Paper>
      </Container>
    );
  }

  if (loading || (order && (order as any).pending)) {
    return (
      <Container maxWidth="sm" className={classes.container}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginTop: 20 }}>
            {(order as any)?.pending ? 'Stiamo elaborando il tuo ordine...' : 'Caricamento ordine...'}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
            Il pagamento è stato completato con successo
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" className={classes.container}>
        <Paper className={classes.paper}>
          <Typography variant="h5" color="error">
            Errore nel recuperare l'ordine
          </Typography>
          <Typography variant="body1" className={classes.message}>
            Non è stato possibile recuperare i dettagli dell'ordine. Se hai completato il pagamento, riceverai comunque
            un'email di conferma.
          </Typography>
          <Box className={classes.buttonBox}>
            <Button variant="outlined" color="primary" component={RouterLink} to="/orders">
              Vai ai miei ordini
            </Button>
            <Button variant="contained" color="primary" component={RouterLink} to="/ordina">
              Torna ai prodotti
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className={classes.container}>
      <Paper className={classes.paper}>
        <CheckCircleIcon className={classes.icon} />

        <Typography variant="h3" className={classes.title}>
          Ordine Confermato!
        </Typography>

        <Typography variant="h6" className={classes.orderNumber}>
          Ordine #{order?.orderNumber}
        </Typography>

        <Typography variant="body1" className={classes.message}>
          Il tuo ordine è stato ricevuto e il pagamento è stato completato con successo.
        </Typography>
        <Typography variant="body1" className={classes.message}>
          Riceverai un'email di conferma con tutti i dettagli all'indirizzo registrato.
        </Typography>

        {order && (
          <Box className={classes.details}>
            <Typography variant="h6" gutterBottom>
              Dettagli ordine:
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Totale pagato:</strong> €{order.total?.toFixed(2)}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Indirizzo di spedizione:</strong>
              <br />
              {order.shippingAddress?.name}
              <br />
              {order.shippingAddress?.address}
              <br />
              {order.shippingAddress?.city}, {order.shippingAddress?.postalCode}, {order.shippingAddress?.province}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DirectSaleConfirmation;
